import { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText, X, RefreshCw, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { supabase } from '../../../../foundation/altzero_base/base/utils/supabaseClient';
import { billService } from '../../services/billService';
import { attachmentService } from '../../services/attachmentService';
import { pickDroppedPdf, validateInvoicePdf } from '../../utils/invoiceDrop';
import { isRenderableFilename } from '../../utils/attachmentPreview';
import { useBlockStrayFileDrop } from '../../utils/useBlockStrayFileDrop';
import type { BvBill } from '../../types/billTypes';
import type { BvAttachment } from '../../types/billValidationTypes';

const BUCKET = 'bv-attachments';

type Props = {
  bill: BvBill;
  onInvoiceUpdated?: (updatedBill: BvBill) => void;
  onPathReady?: (storagePath: string | null) => void;
  onUploadComplete?: (storagePath: string) => void;
};

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .single();
  return data?.organisation_id ?? null;
}

export function InvoicePdfViewer({ bill, onInvoiceUpdated, onPathReady, onUploadComplete }: Props) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Site attachments (invoice / electricity_contract) when no bill-level invoice exists
  const [siteAttachments, setSiteAttachments] = useState<BvAttachment[]>([]);
  const [siteAttachmentsLoading, setSiteAttachmentsLoading] = useState(false);
  const [selectedSitePath, setSelectedSitePath] = useState<string | null>(null);
  const [siteAttachmentUrl, setSiteAttachmentUrl] = useState<string | null>(null);
  const [siteUrlLoading, setSiteUrlLoading] = useState(false);

  // Only while an upload zone is on screen — see useBlockStrayFileDrop.
  useBlockStrayFileDrop(!bill.invoice_path && !selectedSitePath);

  const loadSignedUrl = useCallback(() => {
    if (!bill.invoice_path) return;
    let mounted = true;
    setLoadingUrl(true);
    attachmentService.getSignedUrl(bill.invoice_path, 3600)
      .then((url) => {
        if (!mounted) return;
        setPdfUrl(url);
        setLoadingUrl(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoadingUrl(false);
      });
    return () => { mounted = false; };
  }, [bill.invoice_path]);

  useEffect(() => {
    return loadSignedUrl();
  }, [loadSignedUrl]);

  // Fetch site-level invoice/contract attachments when no bill invoice is attached
  useEffect(() => {
    if (bill.invoice_path || !bill.site_id) return;
    let mounted = true;
    setSiteAttachmentsLoading(true);
    supabase
      .from('bv_attachment')
      .select('*')
      .eq('site_id', bill.site_id)
      .in('category', ['invoice', 'electricity_contract'])
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!mounted) return;
        const attachments = (data ?? []) as BvAttachment[];
        setSiteAttachments(attachments);
        setSiteAttachmentsLoading(false);
      });
    return () => { mounted = false; };
  }, [bill.invoice_path, bill.site_id]);

  const loadSiteAttachmentUrl = useCallback(async (path: string) => {
    setSiteUrlLoading(true);
    setSiteAttachmentUrl(null);
    const url = await attachmentService.getSignedUrl(path, 3600);
    setSiteAttachmentUrl(url);
    setSiteUrlLoading(false);
  }, []);

  useEffect(() => {
    if (bill.invoice_path) onPathReady?.(bill.invoice_path);
  }, [bill.invoice_path, onPathReady]);

  const handleSelectSiteAttachment = (a: BvAttachment) => {
    setSelectedSitePath(a.storage_path);
    onPathReady?.(a.storage_path);
    void loadSiteAttachmentUrl(a.storage_path);
    onUploadComplete?.(a.storage_path);
  };

  const handleFile = async (file: File | null) => {
    const invalid = validateInvoicePdf(file);
    if (invalid || !file) {
      setUploadError(invalid);
      return;
    }

    setUploading(true);
    setUploadError(null);

    const oldPath = bill.invoice_path ?? null;
    let newStoragePath: string | null = null;
    try {
      const orgId = await getOrgId();
      if (!orgId) throw new Error('Organization not found');

      const uid = crypto.randomUUID();
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      newStoragePath = `${orgId}/bills/${bill.id}/${uid}_${safe}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(newStoragePath, file, { upsert: false });
      if (uploadErr) throw uploadErr;

      const res = await billService.saveInvoicePath(bill.id, newStoragePath);
      if (!res.success) {
        // Roll back orphan file in storage since DB save failed
        await supabase.storage.from(BUCKET).remove([newStoragePath]);
        throw new Error(res.error);
      }

      // Remove the old invoice from storage now that DB points to the new one
      if (oldPath) {
        await supabase.storage.from(BUCKET).remove([oldPath]);
      }

      const url = await attachmentService.getSignedUrl(newStoragePath, 3600);
      setPdfUrl(url);
      onPathReady?.(newStoragePath);
      if (res.data && onInvoiceUpdated) onInvoiceUpdated(res.data);
      onUploadComplete?.(newStoragePath);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    void handleFile(pickDroppedPdf(e.dataTransfer));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  // ── Showing a PDF ──────────────────────────────────────────────────────────
  if (bill.invoice_path) {
    return (
      <div className="flex flex-col h-full min-h-[500px]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="font-medium">Retailer Invoice</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1.5"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Replace PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1.5"
              onClick={loadSignedUrl}
              disabled={loadingUrl}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingUrl ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loadingUrl ? (
          <div className="flex flex-col items-center justify-center flex-1 bg-gray-50 rounded-lg border border-gray-200">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin mb-2" />
            <p className="text-sm text-gray-500">Loading invoice…</p>
          </div>
        ) : pdfUrl && isRenderableFilename(bill.invoice_path) ? (
          <iframe
            src={pdfUrl}
            className="flex-1 w-full rounded-lg border border-gray-200 bg-gray-50"
            style={{ minHeight: 500 }}
            title="Retailer Invoice PDF"
          />
        ) : pdfUrl ? (
          // Framing a Word/Excel file downloads it rather than showing it — the
          // same cause as job 18, on the bill-run screen.
          <div className="flex flex-col items-center justify-center flex-1 gap-3 bg-gray-50 rounded-lg border border-gray-200" style={{ minHeight: 500 }}>
            <FileText className="h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-600">This file can't be previewed in the browser</p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-blue-600 hover:bg-gray-50"
            >
              <Upload className="h-4 w-4 rotate-180" /> Open the invoice file
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 bg-red-50 rounded-lg border border-red-200">
            <X className="h-8 w-8 text-red-400 mb-2" />
            <p className="text-sm text-red-600">Could not load PDF. The link may have expired.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={loadSignedUrl}>
              Retry
            </Button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    );
  }

  // ── Site attachment selected — show PDF inline ────────────────────────────
  if (selectedSitePath) {
    return (
      <div className="flex flex-col h-full min-h-[500px]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileText className="h-4 w-4 text-orange-500" />
            <span className="font-medium">Site Invoice</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1.5"
              onClick={() => { setSelectedSitePath(null); setSiteAttachmentUrl(null); onPathReady?.(null); }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1.5"
              onClick={() => void loadSiteAttachmentUrl(selectedSitePath)}
              disabled={siteUrlLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${siteUrlLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {siteUrlLoading ? (
          <div className="flex flex-col items-center justify-center flex-1 bg-gray-50 rounded-lg border border-gray-200">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin mb-2" />
            <p className="text-sm text-gray-500">Loading invoice…</p>
          </div>
        ) : siteAttachmentUrl && isRenderableFilename(selectedSitePath) ? (
          <iframe
            src={siteAttachmentUrl}
            className="flex-1 w-full rounded-lg border border-gray-200 bg-gray-50"
            style={{ minHeight: 500 }}
            title="Site Invoice PDF"
          />
        ) : siteAttachmentUrl ? (
          // Site attachments include electricity contracts, which are routinely
          // Word documents — framing one downloads it (job 18's cause).
          <div className="flex flex-col items-center justify-center flex-1 gap-3 bg-gray-50 rounded-lg border border-gray-200" style={{ minHeight: 500 }}>
            <FileText className="h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-600">This file can't be previewed in the browser</p>
            <a
              href={siteAttachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-blue-600 hover:bg-gray-50"
            >
              <Upload className="h-4 w-4 rotate-180" /> Open the file
            </a>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 bg-red-50 rounded-lg border border-red-200">
            <X className="h-8 w-8 text-red-400 mb-2" />
            <p className="text-sm text-red-600">Could not load PDF. The link may have expired.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => void loadSiteAttachmentUrl(selectedSitePath)}>
              Retry
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── Site attachments picker (no bill-level invoice yet) ───────────────────
  if (siteAttachmentsLoading || siteAttachments.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        {siteAttachmentsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking site attachments…
          </div>
        ) : (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Available from Site Attachments
            </p>
            <div className="space-y-2">
              {siteAttachments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{a.file_name}</p>
                      <p className="text-xs text-gray-400 capitalize">{a.category.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="ml-3 flex-shrink-0 gap-1.5"
                    onClick={() => handleSelectSiteAttachment(a)}
                  >
                    Load & Parse
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs text-gray-400">or upload a dedicated invoice</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <div
          className={`w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-8 gap-3 transition-all cursor-pointer ${
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex items-center gap-3">
            {uploading
              ? <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
              : <Upload className="h-6 w-6 text-blue-600" />}
            <span className="text-sm font-medium text-gray-700">
              {uploading ? 'Uploading…' : 'Upload Invoice PDF'}
            </span>
          </div>
          {uploadError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
              {uploadError}
            </p>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    );
  }

  // ── Upload zone (no bill invoice and no site attachments) ─────────────────
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
      <div
        className={`w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-16 gap-4 transition-all cursor-pointer ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="rounded-full bg-blue-100 p-5">
          {uploading
            ? <Loader2 className="h-9 w-9 text-blue-600 animate-spin" />
            : <FileText className="h-9 w-9 text-blue-600" />}
        </div>
        <div className="text-center px-6">
          <p className="font-semibold text-gray-800 text-base">
            {uploading ? 'Uploading invoice…' : 'Upload Retailer Invoice PDF'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Drop the invoice PDF here, or click to browse
          </p>
          <p className="text-xs text-gray-400 mt-1">PDF format only · Max 20 MB</p>
        </div>
        {!uploading && (
          <Button variant="outline" className="gap-2 pointer-events-none">
            <Upload className="h-4 w-4" />
            Choose File
          </Button>
        )}
        {uploadError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            {uploadError}
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handleInputChange}
      />

      <p className="text-xs text-gray-400 mt-4 text-center max-w-sm">
        Upload the PDF invoice received from your energy retailer.
        It will be stored securely and used for side-by-side comparison with your expected invoice.
      </p>
    </div>
  );
}
